import { auth } from '../services/AuthService.js';
import { permissions } from '../services/PermissionService.js';
import { getEffectiveForecastProbability } from './ForecastUtils.js';

/** Normalize any date-like value to YYYY-MM-DD for range compares (MM-DD-YYYY aware). */
export function toDateKey(value) {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const mdy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (mdy) {
        return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
    }
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) {
        const d = new Date(parsed);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
    return '';
}

function dateInRange(date, rangeStart, rangeEnd) {
    const key = toDateKey(date);
    if (!key) return false;
    if (rangeStart && key < rangeStart) return false;
    if (rangeEnd && key > rangeEnd) return false;
    return true;
}

function scheduleDateForRange(schedule) {
    return schedule.invoiceDate || schedule.dueDate || schedule.plannedDate || schedule.invoice_date || schedule.due_date;
}

function isCancelledSchedule(schedule) {
    return String(schedule?.status || '').toLowerCase() === 'cancelled';
}

/**
 * Unified invoice schedule lines for revenue recognition filter + KPI math.
 * Prefers the active payment model's source so leftover rows from a prior
 * model (e.g. monthly custom schedules after switching to milestone) cannot
 * override the real installments. Falls back to revenue_schedules, then
 * equal-split of invoice/contract amount over schedule_dates.
 */
export function getRevenueScheduleLines(deal, schedules = []) {
    const dealId = String(deal?.id || '');
    if (!dealId) return [];

    const paymentModel = String(deal?.paymentModel || deal?.payment_model || '').toLowerCase();
    const isMilestone = paymentModel === 'milestone';
    const isRecurring = ['monthly', 'weekly', 'quarterly', 'annual'].includes(paymentModel);
    const isOngoing = paymentModel === 'ongoing';
    const isCustomOrOneTime = paymentModel === 'custom' || paymentModel === 'one_time';

    const fromMilestones = () => {
        const lines = [];
        const milestones = deal?.dealMilestones || deal?.milestones || [];
        if (!Array.isArray(milestones) || !milestones.length) return lines;
        milestones.forEach((row) => {
            const date = toDateKey(row.dueDate || row.due_date || row.date);
            if (!date) return;
            const amount = Number(row.amount ?? 0) || 0;
            if (amount > 0) lines.push({ date, amount });
        });
        return lines;
    };

    const fromEmbeddedCustom = () => {
        const lines = [];
        const embeddedCustom = deal?.custom_schedules || deal?.customSchedules || [];
        if (!Array.isArray(embeddedCustom) || !embeddedCustom.length) return lines;
        embeddedCustom.forEach((row) => {
            const date = toDateKey(row.invoice_date || row.invoiceDate || row.date);
            if (!date) return;
            lines.push({ date, amount: Number(row.amount ?? 0) || 0 });
        });
        return lines;
    };

    const fromCustomRows = () => {
        const lines = [];
        const customRows = deal?.customScheduleRows || [];
        if (!Array.isArray(customRows) || !customRows.length) return lines;
        customRows.forEach((row) => {
            const date = toDateKey(row.invoiceDate || row.invoice_date || row.date);
            if (!date) return;
            lines.push({ date, amount: Number(row.amount ?? 0) || 0 });
        });
        return lines;
    };

    const fromPlanAmounts = () => {
        const planDates = deal?.scheduleDates || deal?.schedule_dates || [];
        const planAmounts = deal?.scheduleAmounts || deal?.schedule_amounts || [];
        const hasExplicitAmounts = Array.isArray(planDates) && planDates.length
            && Array.isArray(planAmounts) && planAmounts.length === planDates.length;
        if (!hasExplicitAmounts) return [];
        const lines = [];
        planDates.forEach((value, index) => {
            const date = toDateKey(value);
            if (!date) return;
            lines.push({ date, amount: Number(planAmounts[index]) || 0 });
        });
        return lines;
    };

    const fromRevenueSchedules = () => {
        const dealSchedules = schedules instanceof Map
            ? (schedules.get(dealId) || [])
            : schedules;
        const lines = [];
        for (const schedule of dealSchedules) {
            if (!(schedules instanceof Map)
                && String(schedule?.dealId || schedule?.deal_id || '') !== dealId) {
                continue;
            }
            if (isCancelledSchedule(schedule)) continue;
            const date = toDateKey(scheduleDateForRange(schedule));
            if (!date) continue;
            lines.push({
                date,
                amount: Number(schedule.amount ?? schedule.plannedAmount ?? 0) || 0
            });
        }
        return lines;
    };

    // Payment-model-aware priority — never let stale leftover sources win.
    if (isMilestone) {
        const ms = fromMilestones();
        if (ms.length) return ms;
        const plan = fromPlanAmounts();
        if (plan.length) return plan;
    } else if (isRecurring || isOngoing || isCustomOrOneTime || !paymentModel) {
        const embedded = fromEmbeddedCustom();
        if (embedded.length) return embedded;
        const rows = fromCustomRows();
        if (rows.length) return rows;
        const plan = fromPlanAmounts();
        if (plan.length) return plan;
        if (isCustomOrOneTime || !paymentModel) {
            const ms = fromMilestones();
            if (ms.length) return ms;
        }
    } else {
        // Unknown model: try plan amounts, then custom, then milestones.
        const plan = fromPlanAmounts();
        if (plan.length) return plan;
        const embedded = fromEmbeddedCustom();
        if (embedded.length) return embedded;
        const rows = fromCustomRows();
        if (rows.length) return rows;
        const ms = fromMilestones();
        if (ms.length) return ms;
    }

    const fromRevenue = fromRevenueSchedules();
    if (fromRevenue.length) return fromRevenue;

    if (isMilestone) {
        // Already tried milestones above; fall through to equal-split only.
    } else {
        const ms = fromMilestones();
        if (ms.length) return ms;
    }

    const planDates = deal?.scheduleDates || deal?.schedule_dates || [];
    if (!Array.isArray(planDates) || !planDates.length) return [];

    const perInvoice = Number(deal?.invoiceAmount ?? deal?.invoice_amount ?? 0) || 0;
    const fallbackAmount = perInvoice > 0
        ? perInvoice
        : (Number(deal?.contractValue ?? deal?.dealAmount ?? deal?.amount ?? 0) || 0) / planDates.length;

    const equalSplit = [];
    planDates.forEach((value) => {
        const date = toDateKey(value);
        if (!date) return;
        equalSplit.push({ date, amount: fallbackAmount });
    });
    return equalSplit;
}

function dealMatchesRevenueRange(deal, schedules, rangeStart, rangeEnd) {
    const lines = getRevenueScheduleLines(deal, schedules);
    if (!lines.length) return false;
    if (!rangeStart && !rangeEnd) return true;
    return lines.some(line => dateInRange(line.date, rangeStart, rangeEnd));
}

function sameOwnerId(a, b) {
    if (a == null || b == null || a === '' || b === '') return false;
    return String(a) === String(b);
}

/**
 * Hard gate: Own never shows another user's deals (including a manager's).
 * Team is self + direct reports only. All is unfiltered.
 */
export function restrictDealsToRecordScope(deals, { recordScope, scopedOwnerIds } = {}) {
    const session = auth.getSession();
    if (!session?.userId) return [];
    const list = Array.isArray(deals) ? deals : [];
    const scope = recordScope || permissions.getModuleScope('deals');
    if (scope === 'all') return list;

    if (scope === 'own') {
        return list.filter(d => sameOwnerId(d.ownerUserId, session.userId));
    }

    if (scope === 'team') {
        const allowed = new Set((scopedOwnerIds || []).map(id => String(id)).filter(Boolean));
        if (!allowed.size) return list;
        allowed.add(String(session.userId));
        return list.filter(d => d.ownerUserId && allowed.has(String(d.ownerUserId)));
    }

    return list;
}

function indexSchedulesByDealId(schedules) {
    if (schedules instanceof Map) return schedules;
    const map = new Map();
    for (const schedule of schedules || []) {
        const id = String(schedule?.dealId || schedule?.deal_id || '');
        if (!id) continue;
        let list = map.get(id);
        if (!list) {
            list = [];
            map.set(id, list);
        }
        list.push(schedule);
    }
    return map;
}

export function filterDealsForViews(deals, schedules, filters = {}) {
    let result = restrictDealsToRecordScope(deals, filters);

    const session = auth.getSession();

    if (filters.scope === 'my' && session) {
        result = result.filter(d => d.ownerUserId === session.userId);
    }

    if (filters.ownerIds?.length) {
        const ownerIds = new Set(filters.ownerIds);
        result = result.filter(d => ownerIds.has(d.ownerUserId));
    }

    if (filters.accountIds?.length) {
        const accountIds = new Set(filters.accountIds);
        result = result.filter(d => accountIds.has(d.clientId));
    }

    const dimension = filters.dimension || 'created';
    const scheduleIndex = dimension === 'revenue' ? indexSchedulesByDealId(schedules) : schedules;

    if (!filters.dateRangeAll) {
        if (dimension === 'created') {
            if (filters.startDate) {
                result = result.filter(d => {
                    const date = toDateKey(d.dealDate || d.originalCreatedDate || (d.createdAt && d.createdAt.split('T')[0]));
                    return date && date >= filters.startDate;
                });
            }
            if (filters.endDate) {
                result = result.filter(d => {
                    const date = toDateKey(d.dealDate || d.originalCreatedDate || (d.createdAt && d.createdAt.split('T')[0]));
                    return date && date <= filters.endDate;
                });
            }
        }

        if (dimension === 'revenue') {
            const rangeStart = toDateKey(filters.startDate) || filters.startDate || '';
            const rangeEnd = toDateKey(filters.endDate) || filters.endDate || '';
            if (rangeStart || rangeEnd) {
                result = result.filter(d => dealMatchesRevenueRange(d, scheduleIndex, rangeStart, rangeEnd));
            }
        }
    } else if (dimension === 'revenue') {
        result = result.filter(d => getRevenueScheduleLines(d, scheduleIndex).length > 0);
    }

    if (filters.search) {
        const q = filters.search.toLowerCase().trim();
        if (q) {
            result = result.filter(d =>
                (d.dealName || d.name || '').toLowerCase().includes(q) ||
                (d.clientName || d.accountName || d.client_name || '').toLowerCase().includes(q) ||
                (d.dealContact || d.contactName || d.primaryContact || '').toLowerCase().includes(q) ||
                (d.dealOwner || d.ownerName || d.owner || '').toLowerCase().includes(q) ||
                (d.dealPipeline || '').toLowerCase().includes(q) ||
                (d.dealStage || '').toLowerCase().includes(q)
            );
        }
    }

    return result;
}

export function filterDealsByScope(deals, { ownerUserId } = {}) {
    const scoped = restrictDealsToRecordScope(deals);
    if (!ownerUserId) return scoped;
    return scoped.filter(d => sameOwnerId(d.ownerUserId, ownerUserId));
}

export function legacyFilterCompat(deals, legacyFilters, schedules = []) {
    return filterDealsForViews(deals, schedules, {
        ...legacyFilters,
        scope: legacyFilters.scope || 'all',
        dimension: legacyFilters.dimension || 'created'
    });
}

export function sumRevenueRecognitionAmount(deal, schedules = [], filters = {}) {
    const rangeStart = filters.dateRangeAll
        ? ''
        : (toDateKey(filters.startDate) || filters.startDate || '');
    const rangeEnd = filters.dateRangeAll
        ? ''
        : (toDateKey(filters.endDate) || filters.endDate || '');

    return getRevenueScheduleLines(deal, schedules)
        .filter(line => filters.dateRangeAll || !rangeStart && !rangeEnd || dateInRange(line.date, rangeStart, rangeEnd))
        .reduce((sum, line) => sum + line.amount, 0);
}

export function getRevenueRecognitionForecast(deal, amount) {
    const prob = getEffectiveForecastProbability(deal);
    return (amount || 0) * (prob / 100);
}
